import React, { useState, useEffect } from 'react';
import { getFaqs, createFaq, updateFaq, deleteFaq } from '../../lib/api';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    order: 0,
  });

  const fetchFaqs = async () => {
    try {
      const res = await getFaqs();
      setFaqs(res.data);
    } catch (error) {
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        await updateFaq(editingFaq.id, formData);
        toast.success('FAQ updated successfully');
      } else {
        await createFaq({ ...formData, order: faqs.length });
        toast.success('FAQ created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || '',
      order: faq.order,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await deleteFaq(id);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: '',
      order: 0,
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif text-[#0A0A0A]">FAQs</h1>
          <p className="text-gray-500">Manage frequently asked questions</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-faq-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      <div className="bg-white border border-gray-100">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : faqs.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {faqs.map((faq, index) => (
              <div key={faq.id} className="p-6 hover:bg-gray-50" data-testid={`faq-item-${faq.id}`}>
                <div className="flex items-start gap-4">
                  <div className="text-gray-300">
                    <GripVertical className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-[#0A0A0A] mb-2">{faq.question}</h3>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                    {faq.category && (
                      <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1">
                        {faq.category}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(faq)} className="p-2 text-gray-400 hover:text-[#0A0A0A]">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(faq.id)} className="p-2 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <p>No FAQs yet. Click "Add FAQ" to create one.</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                rows={4}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category (Optional)</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Shipping, Payments"
              />
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary flex-1">
                {editingFaq ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
